import React, { FC, ReactNode, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import { find, propEq } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { slug } from 'to-case';

import { ColorsFilter, FilterByButton, Heading, MenuTitle, Text, TextItemsFilter } from '../../../components/common';
import { ColorIndicator } from '../../../components/common/ColorIndicator';
import { Color } from '../../../constants/Color';
import { FilterName } from '../../../constants/FilterName';
import { mapIndexed } from '../../../utils/array';
import { stopPropagation } from '../../../utils/dom/domEvents';
import { useTrackSearchAndFiltersAnalytics } from '../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics';
import { IExtendedFilter, IFilterItem, IOuter } from './ExtendedItemsFilters.types';
import styles from './ExtendedItemsFilters.scss';

const SubTitle: FC = ({ children }) => (
  <Text
    className={styles.subTitle}
    weight="thin"
    size="medium"
    secondary
    dataHook="extended-filters-title"
    tagName="div"
  >
    {children}
  </Text>
);

export const ExtendedItemsFilters: FC<IOuter> = ({
  sourceName,
  title,
  customFilters = [],
  dataHook = 'extended-items-filter',
}) => {
  const { trackSearchAndFiltersFilterClick } = useTrackSearchAndFiltersAnalytics();

  const onFilterByButtonClick = useCallback(
    (filterName) => (_event, isOpen) => {
      if (isOpen) {
        trackSearchAndFiltersFilterClick({ target: filterName });
      }
    },
    [trackSearchAndFiltersFilterClick],
  );

  const filters = useMemo(() => {
    const customFilterNodes = mapIndexed((customFilter: IExtendedFilter, i: number) => {
      const {
        name: filterName,
        activeItemId,
        menuItems = [],
        menuTitle,
        activeItemLabel,
        columnsCount,
        onItemClick,
        options,
      } = customFilter;

      const currentOption = find<IFilterItem>(propEq('id', activeItemId), menuItems);
      const isColorFilter = filterName === FilterName.Color;
      const isColorSelected = activeItemId !== Color.Any;

      return (
        <span key={i} className={classnames(styles.filterButton, styles.new)}>
          <FilterByButton
            forSmartGrid
            label={
              activeItemLabel ? (
                isColorFilter ? (
                  <span className={styles.colorFilterLabelWrapper}>
                    {isColorSelected && <ColorIndicator color={activeItemId as Color} />}
                    <span className={styles.colorFilterLabelText}>{activeItemLabel}</span>
                  </span>
                ) : (
                  activeItemLabel
                )
              ) : currentOption ? (
                currentOption.name
              ) : (
                ''
              )
            }
            onClick={onFilterByButtonClick(filterName)}
            dataHook="filter-by-button"
            dataFilterType={slug(filterName)}
            labelStyle={isColorFilter ? { paddingLeft: 9 } : {}}
            menuContent={
              <>
                <MenuTitle text={menuTitle} />

                <div className={styles.itemsWrapper}>
                  {isColorFilter ? (
                    <ColorsFilter
                      isAnyColorFilterVisible={options.isAnyColorFilterVisible}
                      isBlackWhiteFilterVisible={options.isBlackWhiteFilterVisible}
                      customColors={options.customColors}
                      selectedColor={activeItemId as Color}
                      onColorSelect={onItemClick}
                    />
                  ) : (
                    <TextItemsFilter
                      items={menuItems}
                      activeItemId={activeItemId}
                      onClick={onItemClick}
                      columnsCount={columnsCount}
                    />
                  )}
                </div>
              </>
            }
          />
        </span>
      );
    }, customFilters) as ReactNode[];

    return (
      <div className={classnames(styles.filters)}>
        <Text className={styles.filterBy} weight="thin" size="medium" secondary>
          <FormattedMessage id="filters.filter-by" />
        </Text>

        {customFilterNodes}
      </div>
    );
  }, [customFilters, onFilterByButtonClick]);

  return (
    <>
      <div className={styles.headingWrapper} data-hook={dataHook} onClick={stopPropagation}>
        {sourceName && (
          <Heading size="medium" dataHook="extended-filters-source-name" className={styles.title}>
            {sourceName}
          </Heading>
        )}
        {title && <SubTitle>{title}</SubTitle>}
      </div>

      <div className={styles.filterWrapper} data-hook="filter-wrapper" onClick={stopPropagation}>
        {filters}
      </div>
    </>
  );
};
